import { takeSseFault, buildFaultErrorFrame } from "./sseFault";

export interface SseChatHandlers {
  onOpen?: (traceId: string | null) => void;
  onFrame?: (data: string) => void;
  onActivity?: () => void;
  onBuffered?: (body: string, contentType: string) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
}

const toError = (err: unknown): Error => (err instanceof Error ? err : new Error(String(err)));

const isEventStream = (contentType: string | null): boolean =>
  !!contentType && contentType.toLowerCase().includes("text/event-stream");

export async function openSseChatStream(
  url: string,
  body: string,
  headers: Record<string, string>,
  signal: AbortSignal,
  handlers: SseChatHandlers,
): Promise<void> {
  const { onOpen, onFrame, onActivity, onBuffered, onError, onClose } = handlers;

  const fault = takeSseFault();
  if (fault) {
    if (fault.mode === "netfail") {
      if (!signal.aborted) onError?.(new Error("[sse-fault] simulated network failure"));
      onClose?.();
      return;
    }
    if (fault.mode === "status") {
      onError?.(new Error(`SSE ${fault.code ?? 500}: [sse-fault] simulated status`));
      onClose?.();
      return;
    }
    if (fault.mode === "buffered") {
      onBuffered?.("[sse-fault] simulated non-stream body", "text/plain");
      onClose?.();
      return;
    }
    if (fault.mode === "errorFrame") {
      onOpen?.(null);
      onFrame?.(buildFaultErrorFrame(fault));
      onClose?.();
      return;
    }
    if (fault.mode === "stall") {
      if (fault.phase !== "connect") onOpen?.(null);
      await new Promise<void>((resolve) => {
        if (signal.aborted) return resolve();
        signal.addEventListener("abort", () => resolve(), { once: true });
      });
      onClose?.();
      return;
    }
    if (fault.mode === "truncate") {
      onOpen?.(null);
      onClose?.();
      return;
    }
  }

  let response: Response;
  try {
    response = await fetch(url, { method: "POST", headers, body, signal });
  } catch (err) {
    if (signal.aborted) onClose?.();
    else {
      onError?.(toError(err));
      onClose?.();
    }
    return;
  }

  const traceId = response.headers.get("x-trace-id");
  const contentType = response.headers.get("content-type");

  if (!response.ok) {
    let detail = `status ${response.status}`;
    try {
      detail = (await response.text()) || detail;
    } catch {}
    onError?.(new Error(`SSE ${response.status}: ${String(detail).slice(0, 300)}`));
    onClose?.();
    return;
  }

  if (!isEventStream(contentType) || !response.body) {
    try {
      onBuffered?.(await response.text(), contentType || "");
    } catch (err) {
      onError?.(toError(err));
    }
    onClose?.();
    return;
  }

  onOpen?.(traceId);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const emit = (block: string) => {
    const dataLines: string[] = [];
    for (const line of block.split("\n")) {
      if (line.startsWith(":")) continue;
      if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
    }
    if (dataLines.length) onFrame?.(dataLines.join("\n"));
  };

  let faultFrames = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      onActivity?.();
      buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, "\n");
      let boundary: number;
      while ((boundary = buffer.indexOf("\n\n")) !== -1) {
        emit(buffer.slice(0, boundary));
        buffer = buffer.slice(boundary + 2);
        if (fault?.mode === "drop") {
          faultFrames += 1;
          if (faultFrames >= (fault.afterFrames ?? 3)) {
            throw new Error("[sse-fault] simulated mid-stream drop");
          }
        }
      }
    }
    if (buffer.trim()) emit(buffer);
  } catch (err) {
    if (!signal.aborted) onError?.(toError(err));
  }
  onClose?.();
}
